import type {ReactNode} from 'react'
import moment from 'moment'
import _ from 'lodash'

import type {PopupRef} from '../UI/overlays/Popup.js'
import type {PdfModalRef} from '../UI/overlays/PdfModal.js'
import {labelStartInput} from './LabelStartPicker.js'

/** What a label action gives: a base64 PDF of the labels (none when all failed) and the errors of single ones */
export interface LabelPrintResult {
    pdf?: string | null
    errors?: string[]
}

/** The callback of ParcelActions.printLabels and OrderActions.printLabels */
export type LabelCallback = (err: unknown, pdf?: string | null, errors?: string[]) => void

export interface PrintLabelsOptions {
    /** The parcels or orders to print the labels of */
    ids: string[]
    printerPageSize: string
    /** A Popup of its own for the print: start position, progress and the errors */
    printPopup: PopupRef
    /** Shows the PDF of a print in one go */
    pdfModal: Pick<PdfModalRef, 'open'>
    /** Gets the labels of these ids. Rejects with the error to show, see fromLabelCallback for the callback actions. */
    print: (ids: string[], nrOfEmptyLabels: number) => Promise<LabelPrintResult>
    /** The popup text of a print in one go, and after the warning ('' by default) */
    text?: string
    /** 'zendingen' or 'ritten', in the text of a print in sets */
    itemName: string
    /** More ids than this are printed in sets of it, each set is downloaded instead of shown (default 50) */
    batchAmount?: number
    /** On confirm, before the first label is requested (after the warning), e.g. clearing the selection */
    onPrint?: () => void
    /**
     * A question on another popup after confirming, when show() is true (the parcels: unknown addresses). Cancelling
     * it stops the print, confirming it reopens the print popup and prints. The popup is also closed on every print.
     */
    warning?: {popup: PopupRef, title: string, text: ReactNode, show: () => boolean}
    /** Sets: also show the message of the backend's progress events, not only the percentage (default true) */
    batchProgressMessages?: boolean
    /** Sets: the progress bar is hidden at the start of every set, until the backend reports progress (default false) */
    hideProgressAtSetStart?: boolean
}

const title = 'Labels printen'

/** For an action with a LabelCallback: resolves with its pdf and errors, rejects with its err */
export function fromLabelCallback(start: (callback: LabelCallback) => void): Promise<LabelPrintResult> {
    return new Promise((resolve, reject) => {
        start((err, pdf, errors) => {
            if (err) {
                reject(err)
            } else {
                resolve({pdf, errors})
            }
        })
    })
}

/** Saves a base64 PDF in the downloads */
export function downloadPdf(pdf: string, fileName: string) {
    const link = document.createElement('a')
    link.href = `data:application/pdf;base64,${pdf}`
    link.download = fileName
    link.click()
    link.remove()
}

/**
 * Prints the labels of parcels or orders: asks for confirmation (and the start position on an A4 sheet) in
 * printPopup, then prints them in one go and shows the PDF in pdfModal, or with more than batchAmount in sets that
 * are downloaded one by one with the progress per set.
 */
export function printLabels(options: PrintLabelsOptions) {
    const {ids, printerPageSize, printPopup, pdfModal, print, text = '', itemName, batchAmount = 50, onPrint, warning, batchProgressMessages = true, hideProgressAtSetStart = false} = options

    const batches = ids.length > batchAmount ? _.chunk(ids, batchAmount) : null
    const progressMessages = batches ? batchProgressMessages : true

    // Once closed (by the user too) the popup stays closed: later progress and results of the print would reopen it
    let isOpen = false
    const whenOpen = (call: () => void) => {
        if (isOpen) {
            call()
        }
    }

    const openPopup = (popupText: string, nrOfEmptyLabels: number, onConfirm: (value: unknown) => void) => {
        const input = labelStartInput(printerPageSize, nrOfEmptyLabels)

        if (input) {
            printPopup.setInput(input)
        }

        isOpen = true
        printPopup.open(title, popupText, onConfirm, () => {
            isOpen = false
        })
        printPopup.listenToProgress({message: progressMessages})
    }

    const printAll = async (nrOfEmptyLabels: number) => {
        let result: LabelPrintResult

        try {
            result = await print(ids, nrOfEmptyLabels)
        } catch (err) {
            whenOpen(() => printPopup.setError(err as ReactNode))
            return
        }

        if (result.errors?.length) {
            whenOpen(() => printPopup.setErrors(result.errors))
        } else {
            whenOpen(() => printPopup.close())
        }

        if (result.pdf) {
            pdfModal.open(result.pdf)
        }
    }

    const printBatches = async (nrOfEmptyLabels: number) => {
        let batchIndex = 0
        // The errors of all sets so far, a set's errors don't replace those of the sets before it
        const errors: string[] = []

        for (const batch of batches) {
            whenOpen(() => printPopup.setProgress(0, hideProgressAtSetStart ? 0 : 100, `Label set ${batchIndex + 1} van ${batches.length}`))

            let result: LabelPrintResult

            try {
                result = await print(batch, nrOfEmptyLabels)
            } catch (err) {
                // Stops the print, the popup stays open with this error and those of the sets before
                whenOpen(() => printPopup.setError(err as ReactNode))
                return
            }

            if (result.errors?.length) {
                errors.push(...result.errors)
                whenOpen(() => printPopup.setErrors([...errors]))
            }

            batchIndex += 1
            whenOpen(() => printPopup.setProgress(100, 100, `Label set ${batchIndex} van ${batches.length}`))

            if (result.pdf) {
                downloadPdf(result.pdf, `labels-${moment().format('YYYY-MM-DD')}-${batchIndex}.pdf`)
            }
        }

        if (errors.length) {
            // Stays open with the errors, like a print in one go
            whenOpen(() => printPopup.setErrors([...errors]))
        } else {
            // A moment to see the last set done
            setTimeout(() => whenOpen(() => printPopup.close()), 1000)
        }
    }

    const start = (nrOfEmptyLabels: number) => {
        warning?.popup.close()
        onPrint?.()

        return batches ? printBatches(nrOfEmptyLabels) : printAll(nrOfEmptyLabels)
    }

    const onConfirm = (value: unknown) => {
        // The picker's value, true without a picker
        const nrOfEmptyLabels = typeof value === 'number' ? value : 0

        if (!warning?.show()) {
            start(nrOfEmptyLabels)
            return
        }

        printPopup.close()
        warning.popup.open(warning.title, undefined, () => {
            // Printing right away: the start position is shown, the confirm button spins
            openPopup(text, nrOfEmptyLabels, () => {})
            printPopup.setLoading(true)
            start(nrOfEmptyLabels)
        })
        warning.popup.setWarning(warning.text)
    }

    openPopup(batches ? `Er zijn meer dan ${batchAmount} ${itemName} geselecteerd. Hierdoor zullen de labels per set van ${batchAmount} worden opgevraagd en gedownload. Zie je download folder om de labels te openen.` : text, 0, onConfirm)
}
